import React from 'react';
import { Alert, Card, Collapse, Tabs, Typography } from 'antd';
import { useAuthStore } from '../store/authStore';

const { Title, Paragraph, Text } = Typography;

type RoleKey = 'employee' | 'customer' | 'supplier' | 'delivery' | 'support' | 'admin';

const roleLabels: Record<RoleKey, string> = {
  employee: 'Сотрудник',
  customer: 'Заказчик',
  supplier: 'Поставщик',
  delivery: 'Доставщик',
  support: 'Поддержка',
  admin: 'Администратор'
};

function GuideContent({ role }: { role: RoleKey }) {
  const blocks: Record<RoleKey, { title: string; items: Array<{ q: string; a: React.ReactNode }> }> = {
    employee: {
      title: 'Как заказать обед',
      items: [
        {
          q: 'Где выбрать блюда?',
          a: (
            <>
              <Text strong>Меню</Text> → выберите дату → добавьте блюда (и/или комбо).
            </>
          )
        },
        {
          q: 'Как отменить/изменить заказ?',
          a: (
            <>
              В <Text strong>Меню</Text> откройте <Text strong>Мои заказы</Text> и удалите позиции до дедлайна отмены
              (если он установлен заказчиком/поставщиком).
            </>
          )
        },
        {
          q: 'Напоминания о заказе (Web Push)',
          a: (
            <>
              <Text strong>Профиль</Text> → включите <Text strong>Напоминания</Text> и выберите время. Уведомление придёт,
              если вы ещё не заказали на сегодня.
            </>
          )
        }
      ]
    },
    customer: {
      title: 'Как управлять организацией и заказами',
      items: [
        { q: 'Сотрудники', a: <>Раздел <Text strong>Мои сотрудники</Text> — управление пользователями и лимитами.</> },
        { q: 'Поставщики', a: <>Раздел <Text strong>Мои поставщики</Text> — заявки/связи, выбор поставщиков.</> },
        { q: 'Заказы', a: <>Раздел <Text strong>Заказы</Text> — просмотр статусов, подтверждение получения при передаче.</> }
      ]
    },
    supplier: {
      title: 'Как управлять меню и обработкой заказов',
      items: [
        {
          q: 'Каталог блюд',
          a: (
            <>
              <Text strong>Моё меню</Text> → вкладка <Text strong>Каталог</Text>. Можно импортировать блюда из Excel и
              массово удалять выбранные позиции.
            </>
          )
        },
        {
          q: 'Планирование меню по дням',
          a: (
            <>
              <Text strong>Моё меню</Text> → вкладка <Text strong>Планирование</Text>. Можно добавлять блюда на даты или
              загрузить Excel меню по дням.
            </>
          )
        },
        {
          q: 'Комбо-меню',
          a: (
            <>
              <Text strong>Моё меню</Text> → вкладка <Text strong>Комбо</Text>. Настройка наборов по датам и копирование на
              период.
            </>
          )
        },
        {
          q: 'Чек-лист и упаковка',
          a: (
            <>
              <Text strong>Заказы</Text> → <Text strong>Чек-лист</Text>. Отмечайте упакованные позиции и печатайте QR/наклейки.
            </>
          )
        }
      ]
    },
    delivery: {
      title: 'Как работать доставщику',
      items: [
        { q: 'Где мои заказы?', a: <>Раздел <Text strong>Доставка</Text> — список заказов со статусом “В доставке”.</> },
        {
          q: 'Как завершить доставку?',
          a: (
            <>
              После подтверждения получения заказчиком — отметьте заказ <Text strong>доставленным</Text>.
            </>
          )
        }
      ]
    },
    support: {
      title: 'Как обрабатывать обращения',
      items: [
        { q: 'Тикеты', a: <>Раздел <Text strong>Поддержка</Text> — отвечайте пользователям, закрывайте обращения.</> }
      ]
    },
    admin: {
      title: 'Администрирование',
      items: [
        { q: 'Одобрение регистраций', a: <>Раздел <Text strong>Заявки</Text> — одобрение поставщиков/заказчиков/поддержки.</> },
        { q: 'Документы', a: <>Раздел <Text strong>Документы</Text> — управление юридическими документами и версиями.</> }
      ]
    }
  };

  const cfg = blocks[role];
  return (
    <Card>
      <Title level={3} style={{ marginTop: 0 }}>
        {roleLabels[role]} — {cfg.title}
      </Title>
      <Collapse
        items={cfg.items.map((it, idx) => ({
          key: String(idx),
          label: it.q,
          children: <Paragraph style={{ marginBottom: 0 }}>{it.a}</Paragraph>
        }))}
      />
    </Card>
  );
}

const Guide: React.FC = () => {
  const { user } = useAuthStore();
  const role = (user?.role as RoleKey | undefined) ?? 'employee';
  const [activeRole, setActiveRole] = React.useState<RoleKey>(role);

  React.useEffect(() => {
    setActiveRole(role);
  }, [role]);

  const tabs: RoleKey[] = ['employee', 'customer', 'supplier', 'delivery', 'support', 'admin'];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>
        Инструкция по платформе
      </Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Короткие инструкции по ключевым сценариям"
        description="Выберите роль, чтобы увидеть пошаговые подсказки. Этот раздел можно расширять по мере развития платформы."
      />
      <Tabs
        activeKey={activeRole}
        onChange={(k) => setActiveRole(k as RoleKey)}
        items={tabs.map((k) => ({
          key: k,
          label: roleLabels[k],
          children: <GuideContent role={k} />
        }))}
      />
    </div>
  );
};

export default Guide;

